import type { FxBurst, FxShape } from '@lunary/app-core/flip-variants'

export type { FxBurst, FxShape }

type Particle = {
  x: number
  y: number
  vx: number
  vy: number
  g: number
  drag: number
  life: number
  age: number
  size: number
  color: string
  shape: FxShape
  rot: number
}

export function isLightTheme(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dataset.theme === 'light'
}

export function themeColor(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback
}

export function drawStar(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, rot = 0): void {
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(rot)
  ctx.beginPath()
  for (let j = 0; j < 8; j++) {
    const r = j % 2 ? size * 0.24 : size
    const a = (j * Math.PI) / 4
    ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r)
  }
  ctx.fill()
  ctx.restore()
}

export function fitCanvas(canvas: HTMLCanvasElement): [CanvasRenderingContext2D, number, number] | null {
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  const w = canvas.clientWidth
  const h = canvas.clientHeight
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const bw = Math.round(w * dpr)
  const bh = Math.round(h * dpr)
  if (canvas.width !== bw || canvas.height !== bh) {
    canvas.width = bw
    canvas.height = bh
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, w, h)
  ctx.globalCompositeOperation = isLightTheme() ? 'source-over' : 'lighter'
  ctx.globalAlpha = 1
  return [ctx, w, h]
}

export function defaultColors(shape: FxShape): string[] {
  if (shape === 'puff') return [isLightTheme() ? '#ffffff' : themeColor('--text', '#f5ecdb')]
  const accent = themeColor('--accent', '#e8b04a')
  return [accent, themeColor('--accent-soft', '#f6d98f'), isLightTheme() ? accent : '#fff3cf']
}

export class CardFx {
  private particles: Particle[] = []
  private frame = 0
  private last = 0

  constructor(private readonly canvas: HTMLCanvasElement) {}

  burst(o: FxBurst): void {
    const shape = o.shape ?? 'dot'
    const colors = o.colors ?? defaultColors(shape)
    const cx = o.x ?? this.canvas.clientWidth / 2
    const cy = o.y ?? this.canvas.clientHeight / 2
    for (let i = 0; i < o.n; i++) {
      const a = Math.random() * Math.PI * 2
      const sp = o.speed * (0.35 + Math.random() * 0.85)
      this.particles.push({
        x: cx + (Math.random() - 0.5) * (o.w ?? 40),
        y: cy + (Math.random() - 0.5) * (o.h ?? 60),
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        g: o.gravity ?? 0,
        drag: o.drag ?? 0.965,
        life: o.life * (0.6 + Math.random() * 0.7),
        age: 0,
        size: o.size * (0.55 + Math.random() * 0.9),
        color: colors[i % colors.length] ?? '#f6d98f',
        shape,
        rot: Math.random() * 6,
      })
    }
    if (!this.frame) {
      this.last = performance.now()
      this.frame = requestAnimationFrame(this.tick)
    }
  }

  clear(): void {
    this.particles = []
  }

  destroy(): void {
    cancelAnimationFrame(this.frame)
    this.frame = 0
    this.particles = []
    this.canvas.getContext('2d')?.clearRect(0, 0, this.canvas.width, this.canvas.height)
  }

  private tick = (now: number): void => {
    const dt = Math.min((now - this.last) / 1000, 0.05)
    this.last = now
    const fit = fitCanvas(this.canvas)
    if (!fit) return
    const [ctx] = fit
    this.particles = this.particles.filter((p) => (p.age += dt) < p.life)
    for (const p of this.particles) {
      p.vx *= p.drag
      p.vy = p.vy * p.drag + p.g * dt
      p.x += p.vx * dt
      p.y += p.vy * dt
      p.rot += dt * 0.9
      const k = p.age / p.life
      const alpha = Math.max(0, k < 0.15 ? k / 0.15 : 1 - (k - 0.15) / 0.85)
      ctx.globalAlpha = alpha
      ctx.fillStyle = p.color
      if (p.shape === 'spark') drawStar(ctx, p.x, p.y, p.size * (1 - k * 0.4), p.rot)
      else if (p.shape === 'puff') {
        const r = p.size * (0.6 + k * 0.9)
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r)
        g.addColorStop(0, p.color)
        g.addColorStop(1, 'transparent')
        ctx.globalAlpha = alpha * 0.22
        ctx.fillStyle = g
        ctx.beginPath()
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2)
        ctx.fill()
      } else {
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
        ctx.fill()
      }
    }
    this.frame = this.particles.length ? requestAnimationFrame(this.tick) : 0
  }
}
